import { useState, useEffect } from "react";
const host: string = import.meta.env.VITE_BACKEND_SERVER_URL;

export const DeveloperEmailCampaignDashboard = () => {
  const [campaigns, setCampaigns] = useState({});
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Poll for campaign updates
  useEffect(() => {
    let interval;
    if (activeCampaign) {
      interval = setInterval(() => {
        fetchCampaignStatus(activeCampaign);
      }, 2000); // Update every 2 seconds
    }
    return () => clearInterval(interval);
  }, [activeCampaign]);

  const startEmailCampaign = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${host}/developer/send-sync-reminder`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "Access-Control-Allow-Credentials": "true",
          jwtToken: localStorage.getItem("jwtTokenD"),
        },
      });
      const data = await response.json();

      if (data.success) {
        setActiveCampaign(data.campaignId);
        setCampaigns((prev) => ({
          ...prev,
          [data.campaignId]: data.status,
        }));
      }
    } catch (error) {
      console.error("Failed to start campaign:", error);
    }
    setIsLoading(false);
  };

  const fetchCampaignStatus = async (campaignId) => {
    try {
      const response = await fetch(
        `${host}/developer/email-campaign/${campaignId}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "Access-Control-Allow-Credentials": "true",
            jwtToken: localStorage.getItem("jwtTokenD"),
          },
        }
      );
      const data = await response.json();

      setCampaigns((prev) => ({
        ...prev,
        [campaignId]: data,
      }));
    } catch (error) {
      console.error("Failed to fetch campaign status:", error);
    }
  };

  // fetch all campaigns
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const response = await fetch(`${host}/developer/email-campaigns/all`, {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "Access-Control-Allow-Credentials": "true",
            jwtToken: localStorage.getItem("jwtTokenD"),
          },
        });
        const data = await response.json();
        setCampaigns(data);
      } catch (error) {
        console.error("Failed to fetch campaigns:", error);
      }
    };
    fetchCampaigns();
  }, []);

  const formatTime = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  const campaign = activeCampaign ? campaigns[activeCampaign] : null;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* display all active campaigns */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-4">Active Campaigns</h1>
        <div className="flex flex-wrap gap-4">
          {Object.keys(campaigns).map((campaignId) => (
            <div
              key={campaignId}
              className="bg-white rounded-lg shadow-md p-6 cursor-pointer"
              onClick={() => setActiveCampaign(campaignId)}
            >
              <div className="text-2xl font-bold text-blue-600">
                Campaign {campaignId}
              </div>
              <div className="text-sm text-gray-600">
                {campaigns[campaignId].status}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-4">Email Campaign Dashboard</h1>

        <button
          onClick={startEmailCampaign}
          disabled={isLoading || (campaign && campaign.status === "running")}
          className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white px-4 py-2 rounded"
        >
          {isLoading ? "Starting..." : "Start Email Campaign"}
        </button>
      </div>

      {campaign && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {campaign.sent}
              </div>
              <div className="text-sm text-gray-600">Sent</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {campaign.sending}
              </div>
              <div className="text-sm text-gray-600">Sending</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {campaign.pending}
              </div>
              <div className="text-sm text-gray-600">Pending</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {campaign.failed}
              </div>
              <div className="text-sm text-gray-600">Failed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-600">
                {campaign.total}
              </div>
              <div className="text-sm text-gray-600">Total</div>
            </div>
          </div>

          {campaign.remainingPauseTime > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="text-center">
                <div className="text-lg font-semibold text-yellow-800">
                  Next batch in: {formatTime(campaign.remainingPauseTime)}
                </div>
                <div className="text-sm text-yellow-600">
                  Pausing to avoid spam detection
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {campaign.batches.map((batch, index) => (
              <div key={index} className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold">
                    Batch {batch.batchNumber} ({batch.size} influencers)
                  </h3>
                  <span className="text-sm text-gray-500">
                    {batch.startTime &&
                      new Date(batch.startTime).toLocaleTimeString()}
                  </span>
                </div>

                <div className="grid gap-2">
                  {batch.influencers.map((influencer, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-2 bg-gray-50 rounded"
                    >
                      <div>
                        <span className="font-medium">{influencer.name}</span>
                        <span className="text-sm text-gray-600 ml-2">
                          {influencer.email}
                        </span>
                      </div>
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          influencer.status === "sent"
                            ? "bg-green-100 text-green-800"
                            : influencer.status === "sending"
                            ? "bg-blue-100 text-blue-800"
                            : influencer.status === "failed"
                            ? "bg-red-100 text-red-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {influencer.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
